import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Settings, DollarSign, Users, Plus, Trash2, Save, Clock, Percent, Package } from "lucide-react";

import { API } from '@/config';

export default function SettingsPage() {
  const [laborSettings, setLaborSettings] = useState({
    hours_per_shift: 8.5,
    tax_burden_multiplier: 1.0765,
    default_wage_rates: {}
  });
  const [miscCostTypes, setMiscCostTypes] = useState([]);
  const [newWageType, setNewWageType] = useState('');
  const [newWageRate, setNewWageRate] = useState('');
  const [newMiscType, setNewMiscType] = useState({ name: '', default_cost: 0, cost_method: 'per_unit' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const [laborRes, miscRes] = await Promise.all([
        axios.get(`${API}/rd/settings/labor`),
        axios.get(`${API}/rd/settings/misc-cost-types`)
      ]);
      setLaborSettings(laborRes.data);
      setMiscCostTypes(miscRes.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveLaborSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/rd/settings/labor`, laborSettings);
      toast.success("Labor settings saved");
    } catch (error) {
      toast.error("Failed to save labor settings");
    } finally {
      setSaving(false);
    }
  };

  const handleAddWageRate = () => {
    if (!newWageType.trim() || !newWageRate) return;
    setLaborSettings(prev => ({
      ...prev,
      default_wage_rates: {
        ...prev.default_wage_rates,
        [newWageType.trim()]: parseFloat(newWageRate) || 0
      }
    }));
    setNewWageType('');
    setNewWageRate('');
  };

  const handleUpdateWageRate = (type, rate) => {
    setLaborSettings(prev => ({
      ...prev,
      default_wage_rates: {
        ...prev.default_wage_rates,
        [type]: parseFloat(rate) || 0
      }
    }));
  };

  const handleRemoveWageRate = (type) => {
    setLaborSettings(prev => {
      const newRates = { ...prev.default_wage_rates };
      delete newRates[type];
      return { ...prev, default_wage_rates: newRates };
    });
  };

  const handleSaveMiscCostTypes = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/rd/settings/misc-cost-types`, { types: miscCostTypes });
      toast.success("Miscellaneous cost types saved");
    } catch (error) {
      toast.error("Failed to save misc cost types");
    } finally {
      setSaving(false);
    }
  };

  const handleAddMiscType = () => {
    if (!newMiscType.name.trim()) return;
    setMiscCostTypes(prev => [...prev, {
      id: `misc-type-${Date.now()}`,
      name: newMiscType.name.trim(),
      default_cost: parseFloat(newMiscType.default_cost) || 0,
      cost_method: newMiscType.cost_method
    }]);
    setNewMiscType({ name: '', default_cost: 0, cost_method: 'per_unit' });
  };

  const handleUpdateMiscType = (id, field, value) => {
    setMiscCostTypes(prev => prev.map(t => 
      t.id === id ? { ...t, [field]: field === 'default_cost' ? parseFloat(value) || 0 : value } : t
    ));
  };

  const handleRemoveMiscType = (id) => {
    setMiscCostTypes(prev => prev.filter(t => t.id !== id));
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="text-gray-500">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Settings className="w-6 h-6 text-gray-600" />
        <h1 className="text-2xl font-bold text-gray-800">Settings</h1>
      </div>

      {/* Labor Settings Card */}
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="w-5 h-5 text-indigo-600" />
            Labor Costing Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Calculation Parameters */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">Hours per Shift</Label>
              <div className="flex items-center gap-2 mt-1">
                <Clock className="w-4 h-4 text-gray-400" />
                <Input
                  type="number"
                  step="0.5"
                  value={laborSettings.hours_per_shift}
                  onChange={(e) => setLaborSettings({ ...laborSettings, hours_per_shift: parseFloat(e.target.value) || 8.5 })}
                  className="w-24"
                />
                <span className="text-sm text-gray-500">hours</span>
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium">Tax Burden Multiplier</Label>
              <div className="flex items-center gap-2 mt-1">
                <Percent className="w-4 h-4 text-gray-400" />
                <Input
                  type="number"
                  step="0.0001"
                  value={laborSettings.tax_burden_multiplier}
                  onChange={(e) => setLaborSettings({ ...laborSettings, tax_burden_multiplier: parseFloat(e.target.value) || 1.0765 })}
                  className="w-28"
                />
                <span className="text-xs text-gray-500">(e.g., 1.0765 = 7.65% employer taxes)</span>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 p-3 rounded-lg text-sm text-gray-600">
            <strong>Calculation Formula:</strong> # Employees × Hourly Rate × {laborSettings.hours_per_shift} hours × {laborSettings.tax_burden_multiplier}
          </div>

          {/* Default Wage Rates */}
          <div>
            <Label className="text-sm font-medium mb-2 block">Default Wage Rates by Role</Label>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">Role / Employee Type</th>
                    <th className="text-right px-3 py-2 font-medium text-gray-600 w-32">Hourly Rate</th>
                    <th className="w-12"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {Object.entries(laborSettings.default_wage_rates).map(([type, rate]) => (
                    <tr key={type} className="hover:bg-gray-50">
                      <td className="px-3 py-2">{type}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-end gap-1">
                          <span className="text-gray-400">$</span>
                          <Input
                            type="number"
                            step="0.50"
                            value={rate}
                            onChange={(e) => handleUpdateWageRate(type, e.target.value)}
                            className="h-7 w-20 text-right"
                          />
                          <span className="text-gray-500">/hr</span>
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-7 w-7 p-0 text-gray-400 hover:text-red-500"
                          onClick={() => handleRemoveWageRate(type)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {Object.keys(laborSettings.default_wage_rates).length === 0 && (
                    <tr>
                      <td colSpan="3" className="px-3 py-4 text-center text-gray-400">
                        No wage rates configured. Add one below.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Add New Wage Rate */}
            <div className="flex gap-2 mt-3">
              <Input
                placeholder="Role type (e.g., Line Operator)"
                value={newWageType}
                onChange={(e) => setNewWageType(e.target.value)}
                className="flex-1"
              />
              <div className="flex items-center gap-1">
                <span className="text-gray-400">$</span>
                <Input
                  type="number"
                  step="0.50"
                  placeholder="Rate"
                  value={newWageRate}
                  onChange={(e) => setNewWageRate(e.target.value)}
                  className="w-24"
                />
              </div>
              <Button onClick={handleAddWageRate} disabled={!newWageType.trim() || !newWageRate}>
                <Plus className="w-4 h-4 mr-1" />Add
              </Button>
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={handleSaveLaborSettings} disabled={saving}>
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Labor Settings'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Miscellaneous Cost Types Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Package className="w-5 h-5 text-emerald-600" />
            Miscellaneous Cost Types
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-500">
            Configure standard miscellaneous cost types. "Per Unit" costs are applied directly, while "Bulk" costs are allocated per unit based on shift throughput.
          </p>

          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-gray-600">Cost Type</th>
                  <th className="text-center px-3 py-2 font-medium text-gray-600 w-32">Method</th>
                  <th className="text-right px-3 py-2 font-medium text-gray-600 w-32">Default Cost</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {miscCostTypes.map(type => (
                  <tr key={type.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2">
                      <Input
                        value={type.name}
                        onChange={(e) => handleUpdateMiscType(type.id, 'name', e.target.value)}
                        className="h-7 border-0 bg-transparent p-0 focus-visible:ring-0"
                      />
                    </td>
                    <td className="px-3 py-2 text-center">
                      <select
                        value={type.cost_method}
                        onChange={(e) => handleUpdateMiscType(type.id, 'cost_method', e.target.value)}
                        className="h-7 text-sm border rounded px-2 bg-white"
                      >
                        <option value="per_unit">Per Unit</option>
                        <option value="bulk">Bulk (÷ throughput)</option>
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1">
                        <span className="text-gray-400">$</span>
                        <Input
                          type="number"
                          step="0.01"
                          value={type.default_cost}
                          onChange={(e) => handleUpdateMiscType(type.id, 'default_cost', e.target.value)}
                          className="h-7 w-20 text-right"
                        />
                      </div>
                    </td>
                    <td className="px-2 py-2">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-7 w-7 p-0 text-gray-400 hover:text-red-500"
                        onClick={() => handleRemoveMiscType(type.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
                {miscCostTypes.length === 0 && (
                  <tr>
                    <td colSpan="4" className="px-3 py-4 text-center text-gray-400">
                      No cost types configured. Add one below.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Add New Misc Cost Type */}
          <div className="flex gap-2">
            <Input
              placeholder="Cost type name (e.g., Logistics Fee)"
              value={newMiscType.name}
              onChange={(e) => setNewMiscType({ ...newMiscType, name: e.target.value })}
              className="flex-1"
            />
            <select
              value={newMiscType.cost_method}
              onChange={(e) => setNewMiscType({ ...newMiscType, cost_method: e.target.value })}
              className="h-9 text-sm border rounded px-2"
            >
              <option value="per_unit">Per Unit</option>
              <option value="bulk">Bulk</option>
            </select>
            <div className="flex items-center gap-1">
              <span className="text-gray-400">$</span>
              <Input
                type="number"
                step="0.01"
                placeholder="Default"
                value={newMiscType.default_cost}
                onChange={(e) => setNewMiscType({ ...newMiscType, default_cost: e.target.value })}
                className="w-24"
              />
            </div>
            <Button onClick={handleAddMiscType} disabled={!newMiscType.name.trim()}>
              <Plus className="w-4 h-4 mr-1" />Add
            </Button>
          </div>

          <div className="flex justify-end">
            <Button onClick={handleSaveMiscCostTypes} disabled={saving}>
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Misc Cost Types'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
